import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Grid2 as Grid,
  Card,
  CardMedia,
  CardContent,
  Popover,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import FloridaAddressAutocomplete from "../../dashboard/FloridaAddressAutocomplete";
import LoadPermitTemplateModal from "./components/LoadPermitTemplateModal";
import { ntboRelations, pcaRelations } from "../../../data/permitFormsData";
import { isNocOptionalForCounty } from "../../../functions/availablePermitForms";
import {
  getPermitMappingOnce,
  setQuickActionAddress,
} from "../../../store/actions/dataActions";
import { resolveMunicipalityMatch } from "../../../utils/municipalityMatching";

const SelectFormsStep = ({
  selectedPermitType,
  setSelectedPermitType,
  selectedForms,
  setSelectedForms,
  stormwaterSelection,
  setStormwaterSelection,
  availableForms,
  setDisplayImage,
  cities,
  selectedCity,
  setSelectedCity,
  countyLoading,
  groupedForms,
  privateProviderSignerType,
  setPrivateProviderSignerType,
  county,
  contractors,
  formContractors,
  setFormContractors,
  onCardsScroll,
  headerCondensed,
  nocId,
  fetchCountyPermit,
}) => {
  const dispatch = useDispatch();
  const GROUPS = [ntboRelations, pcaRelations];
  const stormwaterList = ["Individual", "Corporation", "LLC"];
  const privProviderList = ["Individual", "Corporation", "Partnership"];
  const stormwaterMapping = {
    Individual: "stormwater_indiv",
    Corporation: "stormwater_corp",
    LLC: "stormwater_llc",
  };

  const theme = useTheme();
  const isMobileDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumDown = useMediaQuery(theme.breakpoints.down("md"));
  const isMediumUp = useMediaQuery(theme.breakpoints.up("md"));
  const isMedium2Down = useMediaQuery(theme.breakpoints.down("md2"));
  const isMedium2 = isMediumUp && isMedium2Down;

  const availablePermitTypes = useSelector(
    (state) => state.nocData?.available_permit_types,
  );
  const attachingNewPermit = useSelector(
    (state) => state.myPermitsComponent?.attachingNewPermit,
  );
  const quickAddress = useSelector(
    (state) => state.nocData?.quickActions?.address,
  );
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const permitMapping = useSelector((state) => state.nocData?.permit_mapping);

  const [shouldExpandOnScroll, setShouldExpandOnScroll] = useState(false);
  const [expandedPermitForm, setExpandedPermitForm] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [privFormAnchorEl, setPrivFormAnchorEl] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [propertyAddress, setPropertyAddress] = useState({});
  const [propertyInputValue, setPropertyInputValue] = useState("");
  const [municipalityEnrichmentCache, setMunicipalityEnrichmentCache] =
    useState({});
  const [municipalityEnrichmentStatus, setMunicipalityEnrichmentStatus] =
    useState("idle");
  const municipalityEnrichmentCacheRef = useRef({});

  const areMunicipalityListsEqual = useCallback((left = [], right = []) => {
    if (left === right) return true;
    if (!Array.isArray(left) || !Array.isArray(right)) return false;
    if (left.length !== right.length) return false;

    return left.every((item, index) => {
      const other = right[index];
      return JSON.stringify(item) === JSON.stringify(other);
    });
  }, []);

  const getCountySlug = useCallback(
    (address) => {
      return (
        address?.county?.slug ||
        address?.county_slug ||
        county?.toLowerCase()?.replace(/\s+/g, "_") ||
        ""
      );
    },
    [county],
  );

  const getEnrichmentCacheKey = useCallback(
    (address) => {
      return `${getCountySlug(address)}:${address?.parcel_id || ""}`;
    },
    [getCountySlug],
  );

  const cardsAreaRef = useRef(null);
  const stormwaterTriggerRef = useRef(null);
  const shouldOpenStormwaterRef = useRef(false);
  const quickAddressAppliedRef = useRef(false);
  const lastSelectedAddressRef = useRef("");
  const municipalitySelectionIntentRef = useRef("auto");
  const unincorporatedOptionLabel = useMemo(
    () => `${county} County Unincorporated`,
    [county],
  );
  const municipalityOptions = useMemo(() => {
    const normalizedCities = Array.isArray(cities)
      ? cities.filter(Boolean).filter((city) => city !== "")
      : [];

    return [
      unincorporatedOptionLabel,
      ...normalizedCities.filter(
        (city, index, arr) => arr.indexOf(city) === index,
      ),
    ];
  }, [cities, unincorporatedOptionLabel]);
  const isPermitTypeSelected = Boolean(selectedPermitType);
  const isMunicipalityDisabled =
    !isPermitTypeSelected ||
    cities?.length === 0 ||
    attachingNewPermit?.started;

  const clearStoredQuickActionAddress = useCallback(() => {
    dispatch(
      setQuickActionAddress({
        fullStreetAddress: "",
        houseNumber: "",
        streetName: "",
        county: "",
        city: "",
        taxDistrict: "",
        taxDistrictDscr: "",
        municipalities: [],
        parcelID: "",
        coNum: "",
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(getPermitMappingOnce());
  }, [dispatch]);

  useEffect(() => {
    if (!isMediumDown) setExpandedPermitForm(true);
  }, [isMediumDown]);

  useEffect(() => {
    if (shouldExpandOnScroll) setExpandedPermitForm(false);
  }, [shouldExpandOnScroll]);

  const licenseFilters = useMemo(
    () => ({
      electrical_permit: Array.isArray(permitMapping?.electrical_permit)
        ? permitMapping.electrical_permit
        : [],
      gas_permit: Array.isArray(permitMapping?.mechanical_permit)
        ? permitMapping.mechanical_permit
        : [],
      noc: null,
    }),
    [permitMapping],
  );

  // Helper function to filter contractors by license type for specific forms
  const getFilteredContractors = useCallback(
    (formName) => {
      if (!contractors || contractors.length === 0) return [];

      const filter = licenseFilters[formName];

      // If no filter (like NOC), return all contractors
      let filteredList = filter
        ? contractors.filter((contractor) => {
            const licenseNumber =
              contractor?.licenseNumber || contractor?.license_number || "";
            return filter.some((prefix) => licenseNumber.startsWith(prefix));
          })
        : contractors;

      // Deduplicate by license number to avoid showing same contractor multiple times
      const seen = new Map();
      const deduplicated = [];

      for (const contractor of filteredList) {
        const licenseNumber =
          contractor?.licenseNumber || contractor?.license_number || "";
        if (licenseNumber && !seen.has(licenseNumber)) {
          seen.set(licenseNumber, true);
          deduplicated.push(contractor);
        }
      }

      return deduplicated;
    },
    [contractors, licenseFilters],
  );

  const getContractorDisplayName = useCallback((contractor) => {
    if (!contractor) return "";
    return (
      contractor?.businessName ||
      contractor?.dba_name ||
      contractor?.primary_name ||
      `${contractor?.firstName || contractor?.first_name || ""} ${
        contractor?.lastName || contractor?.last_name || ""
      }`
    );
  }, []);

  const normalizeContractorName = useCallback((name) => {
    if (!name) return "";
    return name.toLowerCase().replace(/\s+/g, " ").trim();
  }, []);

  // TEMPORARY: Lock contractor selection across Generator Permit form cards by NAME.
  // This is a stopgap until the separate contractors flow is finished.
  // To revert to the original behavior, remove this block and the `disabled` logic below.
  const lockedContractorName = useMemo(() => {
    if (!formContractors || !contractors?.length) return "";

    const selectedLicenseNumbers =
      Object.values(formContractors).filter(Boolean);

    if (!selectedLicenseNumbers.length) return "";

    const selectedContractor = contractors.find((contractor) => {
      const licenseNumber =
        contractor?.licenseNumber || contractor?.license_number || "";
      return licenseNumber && selectedLicenseNumbers.includes(licenseNumber);
    });

    return normalizeContractorName(
      getContractorDisplayName(selectedContractor),
    );
  }, [
    formContractors,
    contractors,
    getContractorDisplayName,
    normalizeContractorName,
  ]);

  // Check if form should show contractor dropdown
  const shouldShowContractorDropdown = useCallback(
    (formName) => {
      // Only show for Generator Permit in St. Johns County
      if (selectedPermitType !== "generator_permit") return false;

      const countyLower = county?.toLowerCase();
      const isStJohns =
        countyLower === "st_johns" ||
        countyLower === "st johns" ||
        countyLower === "st. johns";

      if (!isStJohns) return false;

      // Only show for specific forms (not generator_worksheet)
      const formsWithContractor = ["electrical_permit", "gas_permit", "noc"];
      return formsWithContractor.includes(formName);
    },
    [selectedPermitType, county],
  );

  // Handle contractor selection for a specific form
  const handleContractorChange = useCallback(
    (formName, contractorId) => {
      setFormContractors((prev) => ({
        ...prev,
        [formName]: contractorId,
      }));
    },
    [setFormContractors],
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  const privProvHandleClose = () => {
    setPrivFormAnchorEl(null);
  };

  useLayoutEffect(() => {
    if (shouldOpenStormwaterRef.current && stormwaterTriggerRef.current) {
      setAnchorEl(stormwaterTriggerRef.current);
      shouldOpenStormwaterRef.current = false;
    }
  }, [selectedForms]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (cardsAreaRef.current) {
        cardsAreaRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 50);

    return () => clearTimeout(timeout);
  }, [selectedPermitType, selectedCity]);

  useEffect(() => {
    if (
      quickAddressAppliedRef.current ||
      attachingNewPermit?.started ||
      !quickAddress?.fullStreetAddress
    ) {
      return;
    }

    const prefilledAddress = {
      address: {
        full_address: quickAddress.fullStreetAddress,
      },
      county_slug: county?.toLowerCase()?.replace(/\s+/g, "_") || "",
      city: quickAddress.city || "",
      TAX_DISTRICT: quickAddress.taxDistrict || "",
      TAX_DIST_DSCR: quickAddress.taxDistrictDscr || "",
      municipalities: Array.isArray(quickAddress.municipalities)
        ? quickAddress.municipalities
        : [],
      parcel_id: quickAddress.parcelID || "",
      county: {
        co_num: quickAddress.coNum || "",
      },
    };

    setPropertyAddress(prefilledAddress);
    setPropertyInputValue(quickAddress.fullStreetAddress);
    setMunicipalityEnrichmentStatus("idle");
    municipalitySelectionIntentRef.current = "auto";
    lastSelectedAddressRef.current =
      quickAddress.parcelID || quickAddress.fullStreetAddress;
    quickAddressAppliedRef.current = true;
  }, [attachingNewPermit?.started, county, quickAddress]);

  useEffect(() => {
    municipalityEnrichmentCacheRef.current = municipalityEnrichmentCache;
  }, [municipalityEnrichmentCache]);

  useEffect(() => {
    if (
      attachingNewPermit?.started ||
      !propertyAddress ||
      Object.keys(propertyAddress).length === 0
    ) {
      return;
    }

    if (!propertyAddress?.address?.full_address) {
      return;
    }

    const countySlug = getCountySlug(propertyAddress);
    const canEnrichMunicipality = countySlug && propertyAddress?.parcel_id;

    if (!canEnrichMunicipality) {
      setMunicipalityEnrichmentStatus("done");
      return;
    }

    const enrichmentCacheKey = getEnrichmentCacheKey(propertyAddress);

    if (municipalityEnrichmentCacheRef.current[enrichmentCacheKey]) {
      const cachedMunicipalityData =
        municipalityEnrichmentCacheRef.current[enrichmentCacheKey];

      setMunicipalityEnrichmentStatus("done");
      setPropertyAddress((prev) => {
        const hasSameMunicipalityData =
          (prev?.TAX_DISTRICT || "") ===
            (cachedMunicipalityData?.TAX_DISTRICT || "") &&
          (prev?.TAX_DIST_DSCR || "") ===
            (cachedMunicipalityData?.TAX_DIST_DSCR || "") &&
          (prev?.municipality ?? null) ===
            (cachedMunicipalityData?.municipality ?? null) &&
          areMunicipalityListsEqual(
            prev?.municipalities || [],
            cachedMunicipalityData?.municipalities || [],
          );

        if (hasSameMunicipalityData) {
          return prev;
        }

        return {
          ...prev,
          ...cachedMunicipalityData,
        };
      });
      return;
    }

    const controller = new AbortController();

    const enrichMunicipality = async () => {
      try {
        setMunicipalityEnrichmentStatus("loading");

        const { data } = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/real-estate/enrich-municipality`,
          {
            currentSession,
            county_slug: countySlug,
            parcel_id: propertyAddress?.parcel_id,
          },
          { withCredentials: true, signal: controller.signal },
        );

        if (!data?.success || !data?.data) {
          setMunicipalityEnrichmentStatus("failed");
          return;
        }

        const municipalityData = {
          TAX_DISTRICT: data.data?.TAX_DISTRICT || "",
          TAX_DIST_DSCR: data.data?.TAX_DIST_DSCR || "",
          municipalities: Array.isArray(data.data?.municipalities)
            ? data.data.municipalities
            : propertyAddress?.municipalities || [],
          municipality: data.data?.municipality || null,
        };

        setMunicipalityEnrichmentCache((prev) => ({
          ...prev,
          [enrichmentCacheKey]: municipalityData,
        }));

        setPropertyAddress((prev) => ({
          ...prev,
          ...municipalityData,
        }));
        setMunicipalityEnrichmentStatus("done");
      } catch (error) {
        if (!axios.isCancel(error)) {
          setMunicipalityEnrichmentStatus("failed");
          console.error("Failed to enrich municipality", error);
        }
      }
    };

    enrichMunicipality();

    return () => {
      controller.abort();
    };
  }, [
    propertyAddress,
    propertyInputValue,
    attachingNewPermit?.started,
    currentSession,
    areMunicipalityListsEqual,
    getCountySlug,
    getEnrichmentCacheKey,
  ]);

  useEffect(() => {
    if (
      attachingNewPermit?.started ||
      !propertyAddress ||
      Object.keys(propertyAddress).length === 0
    ) {
      return;
    }

    if (!county || !cities?.length) {
      return;
    }

    if (!propertyAddress?.address?.full_address) {
      return;
    }

    const enrichmentCacheKey = getEnrichmentCacheKey(propertyAddress);
    const hasCachedEnrichment = Boolean(
      municipalityEnrichmentCacheRef.current[enrichmentCacheKey],
    );
    const countySlug = getCountySlug(propertyAddress);
    const isWaitingForMunicipalityEnrichment =
      countySlug &&
      propertyAddress?.parcel_id &&
      !hasCachedEnrichment &&
      municipalityEnrichmentStatus === "loading";

    if (isWaitingForMunicipalityEnrichment) {
      return;
    }

    const hasTaxDistrictInfo = Boolean(
      propertyAddress?.TAX_DISTRICT || propertyAddress?.TAX_DIST_DSCR,
    );
    const hasExplicitNoMunicipality = propertyAddress?.municipality === null;
    const shouldRespectManualOverride =
      municipalitySelectionIntentRef.current === "manual" &&
      Boolean(selectedCity);
    const shouldKeepClearedMunicipality =
      municipalitySelectionIntentRef.current === "cleared";

    if (shouldKeepClearedMunicipality) {
      return;
    }

    if (hasTaxDistrictInfo && hasExplicitNoMunicipality) {
      if (shouldRespectManualOverride) {
        return;
      }

      if (selectedCity) {
        setSelectedForms([]);
        setStormwaterSelection(null);
        setPrivateProviderSignerType(null);
      }
      setSelectedCity("");
      return;
    }

    const matchedCity = resolveMunicipalityMatch({
      cities,
      municipality: propertyAddress?.municipality,
      taxDistrictDscr: propertyAddress?.TAX_DIST_DSCR,
      taxDistrict: propertyAddress?.TAX_DISTRICT,
      city: propertyAddress?.city,
      municipalities: propertyAddress?.municipalities,
    });

    if (matchedCity) {
      const shouldKeepManualMunicipality =
        municipalitySelectionIntentRef.current === "manual" &&
        selectedCity &&
        selectedCity !== matchedCity;

      if (shouldKeepManualMunicipality || shouldKeepClearedMunicipality) {
        return;
      }

      if (selectedCity && matchedCity !== selectedCity) {
        setSelectedForms([]);
        setStormwaterSelection(null);
        setPrivateProviderSignerType(null);
      }
      setSelectedCity(matchedCity);
    }
  }, [
    propertyAddress,
    propertyInputValue,
    county,
    cities,
    selectedCity,
    setSelectedCity,
    setSelectedForms,
    setStormwaterSelection,
    setPrivateProviderSignerType,
    attachingNewPermit?.started,
    municipalityEnrichmentStatus,
    getCountySlug,
    getEnrichmentCacheKey,
  ]);

  const GROUP_MAP = new Map();
  for (const group of GROUPS) {
    for (const name of group) GROUP_MAP.set(name, group);
  }

  // Helper: Check if NOC can be deselected based on current form selection
  // Returns true if NOC is optional (can be deselected), false if mandatory
  const canDeselectNoc = useCallback(
    (currentSelectedForms) => {
      // Global rule: noc_affidavit always requires noc
      if (currentSelectedForms.includes("noc_affidavit")) {
        return false;
      }

      const isOptionalCounty = isNocOptionalForCounty(county);
      const countyLower = county?.toLowerCase();
      const isStJohns =
        countyLower === "st_johns" ||
        countyLower === "st johns" ||
        countyLower === "st. johns";

      // Counties configured as optional-NOC can always deselect NOC
      if (isOptionalCounty) {
        return true;
      }

      // st_johns + generator_permit: NOC is always optional
      if (isStJohns && selectedPermitType === "generator_permit") {
        return true;
      }

      // For all other cases: NOC is optional ONLY if pp_ntbo (and its bundle) is the only form selected
      // Note: noc_affidavit counts as a real form that requires NOC, so don't exclude it
      const formsWithoutNoc = currentSelectedForms.filter(
        (form) => form !== "noc",
      );

      // NTBO bundle forms that don't require NOC
      const ntboBundle = ["pp_ntbo", "pp_ntbo_cover_sheet"];

      // Check if only NTBO bundle forms are selected (pp_ntbo alone or pp_ntbo + pp_ntbo_cover_sheet)
      const onlyNtboBundleSelected =
        formsWithoutNoc.length > 0 &&
        formsWithoutNoc.every((form) => ntboBundle.includes(form));

      if (onlyNtboBundleSelected) {
        return true;
      }

      // If no forms selected (besides noc), NOC can be deselected
      if (formsWithoutNoc.length === 0) {
        return true;
      }

      // Otherwise NOC is mandatory (including when noc_affidavit is selected)
      return false;
    },
    [county, selectedPermitType],
  );

  const handleCardClick = useCallback(
    (event, item, formName) => {
      const isStormwater = item?.name === "stormwater";
      const isPrivateProvider = item?.name === "private_provider";

      if (isStormwater && !formName && !selectedForms.includes("stormwater")) {
        stormwaterTriggerRef.current = event.currentTarget;
        shouldOpenStormwaterRef.current = true;
      }

      if (
        isPrivateProvider &&
        !formName &&
        !selectedForms.includes("private_provider")
      ) {
        setPrivFormAnchorEl(event.currentTarget);
      }

      setSelectedForms((prevSelectedForms) => {
        // Helper to auto-add NOC if needed after form change
        const maybeAddNoc = (newForms) => {
          // Global rule: noc_affidavit must go with noc
          if (newForms.includes("noc_affidavit") && !newForms.includes(nocId)) {
            return [nocId, ...newForms];
          }

          // Don't add NOC if it's already in the forms
          if (newForms.includes(nocId)) {
            return newForms;
          }

          // Check if NOC should be optional (can be deselected)
          // If it's optional, don't add it
          if (canDeselectNoc(newForms)) {
            return newForms;
          }

          // Otherwise, NOC is mandatory - add it
          return [nocId, ...newForms];
        };

        // ----- bundle behavior -----
        const bundle = GROUP_MAP.get(item?.name);
        if (bundle) {
          const anySelected = bundle.some((b) => prevSelectedForms.includes(b));
          if (anySelected) {
            // remove entire bundle
            return prevSelectedForms.filter((n) => !bundle.includes(n));
          } else {
            // add entire bundle (idempotent) - but only add forms that are actually available
            const availableBundleForms = bundle.filter((formName) => {
              // Check if the form exists in availableForms
              return availableForms.some((form) => form.name === formName);
            });
            const newForms = Array.from(
              new Set([...prevSelectedForms, ...availableBundleForms]),
            );
            return maybeAddNoc(newForms);
          }
        }

        const isAlreadySelected = prevSelectedForms.includes(item?.form_id);

        if (isAlreadySelected) {
          if (isStormwater) {
            setStormwaterSelection(null);
          }
          if (isPrivateProvider) {
            setPrivateProviderSignerType(null);
          }

          return prevSelectedForms.filter((id) => id !== item?.form_id);
        } else {
          if (isStormwater) {
            if (!formName) {
              return [...prevSelectedForms]; // wait for subtype
            } else {
              const newForms = [...prevSelectedForms, item.form_id]; // always "stormwater"
              return maybeAddNoc(newForms);
            }
          }

          if (isPrivateProvider && !formName) {
            return [...prevSelectedForms]; // wait for signer type
          }

          const newForms = [...prevSelectedForms, item.form_id];
          return maybeAddNoc(newForms);
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedForms,
      setSelectedForms,
      setStormwaterSelection,
      setPrivateProviderSignerType,
      setPrivFormAnchorEl,
      availableForms,
      canDeselectNoc,
    ],
  );

  const handleChangePermitForm = (_, isExpanded) => {
    setExpandedPermitForm(isExpanded);
  };

  const selectFormContent = () => (
    <Box sx={{ mt: 1, mb: 2 }}>
      <FormControl fullWidth variant="filled" sx={{ width: "100%", mb: 2 }}>
        <InputLabel>Permit Type</InputLabel>
        <Select
          size="small"
          value={(function () {
            const opts = (availablePermitTypes || []).map((i) => i.name);
            return opts.length > 0 && opts.includes(selectedPermitType)
              ? selectedPermitType
              : "";
          })()}
          renderValue={(selected) => {
            const selectedOption = availablePermitTypes?.find(
              (item) => item.name === selected,
            );
            return (
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "1rem", sm: "1.2rem" },
                }}
              >
                {selectedOption?.label || selected}
              </Typography>
            );
          }}
          onChange={(e) => {
            setSelectedForms([]);
            setStormwaterSelection(null);
            setSelectedPermitType(e.target.value);
            // resetPropertyAddressSelection();
            // setSelectedCity("");
            if (!attachingNewPermit?.started) {
              const nocForm = availableForms.find(
                (form) => form.form_id === nocId,
              );
              if (nocForm && Array.isArray(nocForm.img)) {
                const updatedImageUrls = nocForm?.img || nocForm?.image_urls;
                setDisplayImage(updatedImageUrls);
              }
            } else {
              const signed =
                attachingNewPermit?.packageSnapshot?.documents?.noc?.signed ||
                [];
              if (Array.isArray(signed) && signed.length > 0) {
                setDisplayImage(signed);
              }
            }
          }}
          MenuProps={{
            PaperProps: {
              style: {
                marginTop: "1.5px",
                maxHeight: 150,
                width: "auto",
                maxWidth: "100%",
              },
            },
          }}
        >
          {availablePermitTypes?.map((item, i) => {
            const alreadyUsed =
              attachingNewPermit?.started &&
              Array.isArray(attachingNewPermit?.addedPermits) &&
              attachingNewPermit.addedPermits.includes(item.name);
            return (
              <MenuItem
                key={i}
                value={item.name}
                sx={{
                  fontWeight: selectedPermitType === item ? "bold" : "normal",
                  fontSize: selectedPermitType === item ? "1.1rem" : "inherit",
                  opacity: alreadyUsed ? 0.4 : 1,
                }}
                disabled={alreadyUsed}
              >
                {item.label}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "center",
          alignItems: "stretch",
          flexDirection: { xs: "column", sm: "row" },
          width: "100%",
        }}
      >
        {!attachingNewPermit?.started && (
          <Box sx={{ width: "100%", flex: 1 }}>
            <FloridaAddressAutocomplete
              label="Property Address"
              disabled={!isPermitTypeSelected}
              onSelect={(val) => {
                const nextAddressKey =
                  val?.parcel_id ||
                  val?.address?.full_with_city_county ||
                  val?.address?.full_address ||
                  "";
                const previousAddressKey = lastSelectedAddressRef.current;
                const hasAddressChanged = previousAddressKey !== nextAddressKey;

                if (hasAddressChanged) {
                  municipalitySelectionIntentRef.current = "auto";
                  setSelectedForms([]);
                  setStormwaterSelection(null);
                  setPrivateProviderSignerType(null);
                }

                const nextPropertyAddress = {
                  ...val,
                  county_slug:
                    val?.county?.slug ||
                    val?.county_slug ||
                    county?.toLowerCase()?.replace(/\s+/g, "_") ||
                    "",
                  municipality: undefined,
                };

                const canEnrichMunicipality =
                  getCountySlug(nextPropertyAddress) &&
                  nextPropertyAddress?.parcel_id;
                const enrichmentCacheKey =
                  getEnrichmentCacheKey(nextPropertyAddress);
                const hasCachedEnrichment = Boolean(
                  municipalityEnrichmentCacheRef.current[enrichmentCacheKey],
                );

                setMunicipalityEnrichmentStatus(
                  canEnrichMunicipality && !hasCachedEnrichment
                    ? "loading"
                    : "idle",
                );

                setPropertyAddress(nextPropertyAddress);
                const hasSelectedAddress = !!(
                  val &&
                  Object.keys(val).length > 0 &&
                  val?.address?.full_address
                );
                if (hasSelectedAddress) {
                  dispatch(
                    setQuickActionAddress({
                      fullStreetAddress:
                        val?.address?.full_with_city_county ||
                        val?.address?.full_address ||
                        "",
                      houseNumber: val?.address?.number || "",
                      streetName: val?.address?.street || "",
                      county,
                      city: val?.city || "",
                      taxDistrict: val?.TAX_DISTRICT || "",
                      taxDistrictDscr: val?.TAX_DIST_DSCR || "",
                      municipalities: Array.isArray(val?.municipalities)
                        ? val.municipalities
                        : [],
                      parcelID: val?.parcel_id || val?.parcel_number || "",
                      coNum: val?.county?.co_num || val?.co_num || "",
                    }),
                  );
                } else {
                  clearStoredQuickActionAddress();
                }

                if (!hasSelectedAddress) {
                  if (isPermitTypeSelected) {
                    setSelectedCity("");
                  }
                }

                lastSelectedAddressRef.current = nextAddressKey;
              }}
              address={propertyAddress}
              inputValue={propertyInputValue}
              setInputValue={setPropertyInputValue}
              currentSession={currentSession}
              restrictToCounty={true}
              county={county}
              helperText={"Select a valid address to auto-select municipality."}
            />
          </Box>
        )}

        {/* Cities */}
        <FormControl
          fullWidth
          disabled={isMunicipalityDisabled}
          variant="filled"
          sx={{ width: "100%", flex: 1 }}
        >
          <InputLabel id="remove-NumCounties" shrink>
            {cities?.length === 0
              ? "No Cities Available"
              : attachingNewPermit?.started
                ? "Municipality (Locked to Main Permit)"
                : "Municipality"}
          </InputLabel>
          <Select
            size="small"
            disabled={isMunicipalityDisabled}
            displayEmpty
            value={(function () {
              const opts = Array.isArray(cities) ? cities : [];
              // For additional permits, use the main permit's city (already formatted)
              if (attachingNewPermit?.started) {
                return selectedCity || "";
              }

              if (!selectedCity) {
                return "";
              }

              return (opts.includes(selectedCity) ? selectedCity : "") || "";
            })()}
            labelId="remove-NumCounties"
            label="removeNumCounties"
            onChange={(e) => {
              // Don't allow city change when attaching additional permit
              if (attachingNewPermit?.started) {
                return;
              }
              municipalitySelectionIntentRef.current = "manual";
              setSelectedForms([]);
              setStormwaterSelection(null);
              setSelectedCity(e.target.value);
              fetchCountyPermit(e.target.value);
              if (!attachingNewPermit?.started) {
                const nocForm = availableForms.find(
                  (form) => form.form_id === nocId,
                );
                if (nocForm && Array.isArray(nocForm.img)) {
                  const updatedImageUrls = nocForm?.img || nocForm?.image_urls;
                  setDisplayImage(updatedImageUrls);
                }
              } else {
                const signed =
                  attachingNewPermit?.packageSnapshot?.documents?.noc?.signed ||
                  [];
                if (Array.isArray(signed) && signed.length > 0) {
                  setDisplayImage(signed);
                }
              }
            }}
            renderValue={(selected) => (
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "1rem", sm: "1.2rem" },
                }}
              >
                {selected || unincorporatedOptionLabel}
              </Typography>
            )}
            MenuProps={{
              PaperProps: {
                style: {
                  marginTop: "1.5px",
                  maxHeight: 150, // Set the desired max height for the dropdown menu
                  width: "auto",
                  maxWidth: "100%",
                },
              },
            }}
            endAdornment={
              municipalityEnrichmentStatus === "loading" ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} color="inherit" />
                </InputAdornment>
              ) : undefined
            }
          >
            <MenuItem
              value=""
              sx={{
                fontWeight: !selectedCity ? "bold" : "normal",
                fontSize: !selectedCity ? "1.1rem" : "inherit",
              }}
            >
              {unincorporatedOptionLabel}
            </MenuItem>
            {municipalityOptions
              ?.filter((item) => item !== unincorporatedOptionLabel)
              ?.map(function (item, i) {
                const toTitleCase = (str) => {
                  return str.replace(/\w\S*/g, (txt) => {
                    return (
                      txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
                    );
                  });
                };
                return (
                  <MenuItem
                    key={i}
                    value={item}
                    sx={{
                      fontWeight: selectedCity === item ? "bold" : "normal",
                      fontSize: selectedCity === item ? "1.1rem" : "inherit",
                    }}
                  >
                    {toTitleCase(item)}
                  </MenuItem>
                );
              })}
          </Select>
          <FormHelperText sx={{ minHeight: "1.25rem", visibility: "hidden" }}>
            Municipality helper spacer
          </FormHelperText>
        </FormControl>
      </Box>
    </Box>
  );

  let totalForms;

  if (groupedForms) {
    totalForms = Object.values(groupedForms).reduce((total, forms) => {
      return total + (Array.isArray(forms) ? forms.length : 0);
    }, 0);
  } else {
    totalForms = (availableForms || []).filter(
      (item) => !(attachingNewPermit?.started && item?.form_id === nocId),
    ).length;
  }

  const selectedBase = (selectedForms || []).filter(Boolean).length;
  const shouldSubtractForInitialNoc =
    !selectedPermitType && !attachingNewPermit?.started;
  const selectedCount = Math.max(
    0,
    selectedBase - (shouldSubtractForInitialNoc ? 1 : 0),
  );

  return (
    <>
      <Box>
        {isMediumDown ? (
          <Accordion
            expanded={expandedPermitForm}
            onChange={handleChangePermitForm}
            slotProps={{
              root: {
                sx: {
                  borderRadius: "8px !important",
                  marginBottom: "16px !important",
                  marginTop: "8px !important",
                  boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.2) !important",
                },
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="permit-form-content"
              id="permit-form-header"
              sx={{ fontSize: "14px", fontWeight: 600 }}
            >
              Permit Form
            </AccordionSummary>
            <AccordionDetails sx={{ pb: "0px !important" }}>
              {selectFormContent()}
            </AccordionDetails>
          </Accordion>
        ) : (
          selectFormContent()
        )}

        <Box
          sx={{
            borderBottom: "3px solid #d3d3d3",
          }}
        />
        {countyLoading ? (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "center",
              mt: 10,
            }}
          >
            <CircularProgress color="warning" size="8rem" />
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                <Typography mt={1} mb={1} fontWeight="bold" align="center">
                  Selected {selectedCount}/{totalForms}
                </Typography>
                <Button
                  onClick={() => {
                    // Select All: Get all available form names
                    let allFormNames = [];

                    if (groupedForms) {
                      // If grouped, collect all forms from all groups
                      Object.values(groupedForms).forEach((forms) => {
                        forms
                          ?.filter((item) => {
                            // Hide NOC and NOC Affidavit for additional permits
                            if (
                              attachingNewPermit?.started &&
                              (item?.form_id === nocId ||
                                item?.name === "noc_affidavit")
                            ) {
                              return false;
                            }
                            return true;
                          })
                          ?.forEach((item) => {
                            if (item?.form_id) {
                              allFormNames.push(item.form_id);
                            }
                          });
                      });
                    } else {
                      // If not grouped, use availableForms
                      allFormNames = (availableForms || [])
                        .filter(
                          (item) =>
                            !(
                              attachingNewPermit?.started &&
                              item?.form_id === nocId
                            ),
                        )
                        .map((item) => item.form_id)
                        .filter(Boolean);
                    }

                    // Handle bundle behavior - if a form is in a bundle, add all bundle forms
                    const bundleForms = new Set();
                    allFormNames.forEach((formName) => {
                      const bundle = GROUP_MAP.get(formName);
                      if (bundle) {
                        // Add all forms in the bundle that are available
                        bundle.forEach((bundleFormName) => {
                          if (
                            availableForms.some(
                              (form) => form.name === bundleFormName,
                            )
                          ) {
                            bundleForms.add(bundleFormName);
                          }
                        });
                      } else {
                        bundleForms.add(formName);
                      }
                    });

                    setSelectedForms(Array.from(bundleForms));
                  }}
                  disabled={!selectedPermitType}
                  size="small"
                  variant="text"
                  sx={{ fontWeight: "bold", ml: 0.5 }}
                >
                  Select All
                </Button>
                <Button
                  onClick={() => {
                    // Unselect All: Clear all selected forms
                    // Exclude NOC if it's mandatory
                    // (optional counties can clear everything)
                    const isNocMandatory =
                      selectedPermitType !== "generator_permit" &&
                      !isNocOptionalForCounty(county);

                    if (isNocMandatory && selectedForms.includes(nocId)) {
                      // Keep NOC selected if it's mandatory
                      setSelectedForms([nocId]);
                    } else {
                      // Clear all forms
                      setSelectedForms([]);
                    }

                    // Also clear stormwater and private provider selections
                    setStormwaterSelection(null);
                    setPrivateProviderSignerType(null);
                  }}
                  disabled={!selectedPermitType}
                  size="small"
                  variant="text"
                  sx={{ fontWeight: "bold" }}
                >
                  Unselect All
                </Button>
              </Box>
              {/* TEMPLATES */}
              <Box sx={{ display: "flex", mr: 2, alignItems: "center" }}>
                <Button
                  onClick={() => {
                    setOpenModal(true);
                  }}
                  size="small"
                  variant="contained"
                  fontWeight="bold"
                >
                  Templates
                </Button>
              </Box>
              {/* ######### */}
            </Box>

            <Box
              ref={cardsAreaRef}
              onScroll={(event) => {
                if (!onCardsScroll) return;
                const shouldCondense = event.currentTarget.scrollTop > 20;
                onCardsScroll(shouldCondense);
                if (isMediumDown) {
                  setShouldExpandOnScroll(shouldCondense);
                }
              }}
              sx={{
                height: isMediumDown
                  ? !expandedPermitForm
                    ? isMobileDown
                      ? "calc(100vh - 310px)"
                      : "calc(100vh - 420px)"
                    : headerCondensed
                      ? "calc(100vh - 600px)"
                      : isMobileDown
                        ? "calc(100vh - 460px)"
                        : "calc(100vh - 610px)"
                  : isMedium2
                    ? "calc(100vh - 550px)"
                    : "calc(100vh - 460px)",
                overflowY: "auto",
              }}
            >
              {/* Show grouped view if available */}
              {selectedPermitType && groupedForms ? (
                Object.entries(groupedForms)?.map(([sectionTitle, forms]) => (
                  <Box
                    key={sectionTitle}
                    sx={{
                      mb: 4,
                      borderTop:
                        sectionTitle === "Others"
                          ? "3px solid #d3d3d3"
                          : undefined,
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="primary"
                      mb={2}
                      mt={3}
                      sx={{ px: 1 }}
                    >
                      {sectionTitle === "Others"
                        ? "Optional Forms"
                        : sectionTitle}
                    </Typography>
                    <Grid container spacing={2}>
                      {forms
                        ?.filter((item) => {
                          // Hide NOC and NOC Affidavit for additional permits
                          if (
                            attachingNewPermit?.started &&
                            (item?.form_id === nocId ||
                              item?.name === "noc_affidavit")
                          )
                            return false;
                          return true;
                        })
                        ?.map((item, index) => (
                          <Grid
                            size={{ xs: 6, sm: 6, md: 4, lg: 3 }}
                            key={`${sectionTitle}-${index}`}
                          >
                            <Card
                              sx={{
                                cursor:
                                  // NOC is clickable only if it can be deselected
                                  item?.form_id === nocId &&
                                  !canDeselectNoc(selectedForms)
                                    ? "normal"
                                    : "pointer",
                                border: "3px solid #1976d2",
                                p: { xs: 0.5, sm: 1 },
                                minHeight: { xs: "10.5rem", sm: "14rem" },
                                backgroundColor: selectedForms.includes(
                                  item?.form_id,
                                )
                                  ? "rgba(56, 142, 60, 0.3)"
                                  : "#fff",
                              }}
                              onClick={(event) => {
                                // NOC can only be clicked if it's optional
                                if (
                                  item?.form_id === nocId &&
                                  !canDeselectNoc(selectedForms)
                                )
                                  return;
                                handleCardClick(event, item, null);
                              }}
                            >
                              <CardMedia
                                component="img"
                                height="140"
                                image={item?.cover_img || item?.cover_image_url}
                                alt={item.label}
                                sx={{ height: { xs: 92, sm: 140 } }}
                              />
                              <CardContent
                                sx={{ px: 0, pt: { xs: 0.5, sm: 1 }, pb: 1 }}
                              >
                                <Typography
                                  align="center"
                                  variant="body2"
                                  color="primary"
                                  fontWeight="bold"
                                  sx={{
                                    fontSize: { xs: "0.75rem", sm: "0.9rem" },
                                    display: {
                                      xs: "-webkit-box",
                                      xsm: "block",
                                    },
                                    WebkitLineClamp: { xs: 2, xsm: "unset" },
                                    WebkitBoxOrient: {
                                      xs: "vertical",
                                      xsm: "unset",
                                    },
                                    overflow: {
                                      xs: "hidden",
                                      xsm: "visible",
                                    },
                                    textOverflow: {
                                      xs: "ellipsis",
                                      xsm: "unset",
                                    },
                                  }}
                                >
                                  {item.name === "stormwater" &&
                                  stormwaterSelection
                                    ? item?.forms?.find(
                                        (storm) =>
                                          storm?.name === stormwaterSelection,
                                      )?.label
                                    : item.name === "private_provider" &&
                                        privateProviderSignerType
                                      ? item.label +
                                        " " +
                                        privateProviderSignerType
                                      : item.label}
                                </Typography>

                                {/* Contractor Dropdown for Generator Permit in St. Johns */}
                                {shouldShowContractorDropdown(item?.name) && (
                                  <Box
                                    sx={{ mt: 2, px: 1 }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <FormControl
                                      fullWidth
                                      size="small"
                                      variant="filled"
                                    >
                                      <InputLabel
                                        id={`contractor-select-${item?.name}`}
                                      >
                                        Select Contractor
                                      </InputLabel>
                                      <Select
                                        labelId={`contractor-select-${item?.name}`}
                                        id={`contractor-${item?.name}`}
                                        value={
                                          formContractors?.[item?.name] || ""
                                        }
                                        label="Select Contractor"
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleContractorChange(
                                            item?.name,
                                            e.target.value,
                                          );
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <MenuItem value="">
                                          <em>None</em>
                                        </MenuItem>
                                        {getFilteredContractors(
                                          item?.name,
                                        )?.map((contractor, idx) => {
                                          const licenseNumber =
                                            contractor?.licenseNumber ||
                                            contractor?.license_number ||
                                            "";
                                          const contractorDisplayName =
                                            getContractorDisplayName(
                                              contractor,
                                            );
                                          const normalizedName =
                                            normalizeContractorName(
                                              contractorDisplayName,
                                            );

                                          // TEMPORARY: Disable contractors that do not match the locked name.
                                          // This enforces same-contractor-by-name across form cards.
                                          const isLockedOut =
                                            lockedContractorName &&
                                            normalizedName &&
                                            normalizedName !==
                                              lockedContractorName;
                                          return (
                                            <MenuItem
                                              key={licenseNumber || idx}
                                              value={licenseNumber}
                                              disabled={isLockedOut}
                                            >
                                              {contractorDisplayName}
                                              {licenseNumber
                                                ? ` (${licenseNumber})`
                                                : ""}
                                            </MenuItem>
                                          );
                                        })}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                ))
              ) : availableForms.length > 0 && selectedPermitType ? (
                <Grid container spacing={2} mt={2} mb={2}>
                  {availableForms
                    ?.filter((item) => {
                      if (
                        attachingNewPermit?.started &&
                        (item?.form_id === nocId ||
                          item?.name === "noc_affidavit")
                      )
                        return false;
                      return true;
                    })
                    ?.map((item, index) => (
                      <Grid size={{ xs: 6, sm: 6, md: 4, lg: 3 }} key={index}>
                        <Card
                          sx={{
                            cursor:
                              // NOC is clickable only if it can be deselected
                              item?.form_id === nocId &&
                              !canDeselectNoc(selectedForms)
                                ? "normal"
                                : "pointer",
                            border: "3px solid #1976d2",
                            p: { xs: 0.5, sm: 1 },
                            minHeight: { xs: "10.5rem", sm: "14rem" },
                            backgroundColor: selectedForms.includes(
                              item?.form_id,
                            )
                              ? "rgba(56, 142, 60, 0.3)"
                              : "#fff",
                          }}
                          onClick={(event) => {
                            // NOC can only be clicked if it's optional
                            if (
                              item?.form_id === nocId &&
                              !canDeselectNoc(selectedForms)
                            )
                              return;
                            handleCardClick(event, item, null);
                          }}
                        >
                          <CardMedia
                            component="img"
                            height="140"
                            image={item?.cover_img || item?.cover_image_url}
                            alt={item.label}
                            sx={{ height: { xs: 92, sm: 140 } }}
                          />
                          <CardContent
                            sx={{ px: 0, pt: { xs: 0.5, sm: 1 }, pb: 1 }}
                          >
                            <Typography
                              align="center"
                              variant="body2"
                              color="primary"
                              fontWeight="bold"
                              sx={{
                                fontSize: { xs: "0.75rem", sm: "0.9rem" },
                                display: {
                                  xs: "-webkit-box",
                                  xsm: "block",
                                },
                                WebkitLineClamp: { xs: 2, xsm: "unset" },
                                WebkitBoxOrient: {
                                  xs: "vertical",
                                  xsm: "unset",
                                },
                                overflow: {
                                  xs: "hidden",
                                  xsm: "visible",
                                },
                                textOverflow: {
                                  xs: "ellipsis",
                                  xsm: "unset",
                                },
                              }}
                            >
                              {item.name === "stormwater" && stormwaterSelection
                                ? item?.forms?.find(
                                    (storm) =>
                                      storm?.name === stormwaterSelection,
                                  )?.label
                                : item.name === "private_provider" &&
                                    privateProviderSignerType
                                  ? item.label + " " + privateProviderSignerType
                                  : item.label}
                            </Typography>

                            {/* Contractor Dropdown for Generator Permit in St. Johns */}
                            {shouldShowContractorDropdown(item?.name) && (
                              <Box
                                sx={{ mt: 2, px: 1 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FormControl fullWidth size="small">
                                  <InputLabel
                                    id={`contractor-select-${item?.name}`}
                                  >
                                    Select Contractor
                                  </InputLabel>
                                  <Select
                                    labelId={`contractor-select-${item?.name}`}
                                    id={`contractor-${item?.name}`}
                                    value={formContractors?.[item?.name] || ""}
                                    label="Select Contractor"
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleContractorChange(
                                        item?.name,
                                        e.target.value,
                                      );
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <MenuItem value="">
                                      <em>None</em>
                                    </MenuItem>
                                    {getFilteredContractors(item?.name)?.map(
                                      (contractor, idx) => {
                                        const licenseNumber =
                                          contractor?.licenseNumber ||
                                          contractor?.license_number ||
                                          "";
                                        const contractorDisplayName =
                                          getContractorDisplayName(contractor);
                                        const normalizedName =
                                          normalizeContractorName(
                                            contractorDisplayName,
                                          );

                                        // TEMPORARY: Disable contractors that do not match the locked name.
                                        // This enforces same-contractor-by-name across form cards.
                                        const isLockedOut =
                                          lockedContractorName &&
                                          normalizedName &&
                                          normalizedName !==
                                            lockedContractorName;
                                        return (
                                          <MenuItem
                                            key={licenseNumber || idx}
                                            value={licenseNumber}
                                            disabled={isLockedOut}
                                          >
                                            {contractorDisplayName}
                                            {licenseNumber
                                              ? ` (${licenseNumber})`
                                              : ""}
                                          </MenuItem>
                                        );
                                      },
                                    )}
                                  </Select>
                                </FormControl>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                </Grid>
              ) : (
                availableForms.length === 0 && (
                  <Typography
                    align="center"
                    my={3}
                    variant="h6"
                    sx={{ color: "red" }}
                  >
                    Currently, No forms available for this county yet
                  </Typography>
                )
              )}

              {/* Popover for stormwater form selection */}
              <Popover
                // id={id}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
              >
                <Box
                  sx={{
                    borderRadius: 2,
                    padding: 2,
                    border: "3px solid #d9e6f4",
                  }}
                >
                  <Typography
                    color="primary"
                    variant="h6"
                    align="center"
                    mb={1}
                  >
                    Select Form Type
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                    }}
                  >
                    {stormwaterList?.map((formName) => (
                      <Button
                        key={formName}
                        variant="outlined"
                        onClick={(event) => {
                          setStormwaterSelection(stormwaterMapping[formName]);
                          handleCardClick(
                            event,
                            { name: "stormwater" },
                            formName,
                          );
                          handleClose();
                        }}
                        fullWidth
                      >
                        {formName}
                      </Button>
                    ))}
                  </Box>
                </Box>
              </Popover>

              <Popover
                open={Boolean(privFormAnchorEl)}
                anchorEl={privFormAnchorEl}
                onClose={privProvHandleClose}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
              >
                <Box
                  sx={{
                    borderRadius: 2,
                    padding: 2,
                    border: "3px solid #d9e6f4",
                  }}
                >
                  <Typography
                    color="primary"
                    variant="h6"
                    align="center"
                    mb={1}
                  >
                    Select Signer Type
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                    }}
                  >
                    {privProviderList?.map((signerType, index) => (
                      <Button
                        variant="outlined"
                        key={index}
                        onClick={(event) => {
                          setPrivateProviderSignerType(signerType);
                          handleCardClick(
                            event,
                            {
                              name: "private_provider",
                            },
                            {
                              name: "private_provider",
                            },
                          );
                          privProvHandleClose();
                        }}
                        fullWidth
                      >
                        {signerType}
                      </Button>
                    ))}
                  </Box>
                </Box>
              </Popover>
            </Box>
          </Box>
        )}
      </Box>
      <LoadPermitTemplateModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        selectedForms={selectedForms}
        setSelectedForms={setSelectedForms}
        city={selectedCity}
        setCity={setSelectedCity}
        permitType={selectedPermitType}
        setPermitType={setSelectedPermitType}
        county={county}
      />
    </>
  );
};

export default SelectFormsStep;
